// src/components/ExamenRunner.jsx
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LatexBlock from "./LatexBlock";

// Detecta si el string comienza con http:// o https://
const isUrl = (str) => /^https?:\/\//i.test(str);

/* ---------- util ---------- */
const formatTime = s =>
  `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

/* puntajes por curso */
const puntosPorCurso = (curso,tipo)=>
  (tipo==="diagnostico"
    ? {RM:0.96,RV:0.96,Aritmética:1.81,Álgebra:1.81,Geometría:1.81,Trigonometría:1.81,Física:3.62,Química:2.42}
    : {RM:0.48,RV:0.48,Aritmética:0.91,Álgebra:0.91,Geometría:0.91,Trigonometría:0.91,Física:0.91,Química:0.60}
  )[curso]??0;

/* orden fijo de cursos */
const ORDEN = ["RM","RV","Aritmética","Álgebra","Geometría","Trigonometría","Física","Química"];

/* ---------- header ---------- */
function Header({meta,idx,total,left}){
  const pct = ((idx+1)/total)*100;
  return(
    <header className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-semibold text-lg truncate">
          {meta.titulo || `${meta.tipo} ${meta.numero}`}
        </h2>
        <div className="flex items-center space-x-2">
          <span className="text-lg">⏱️</span>
          <span className="bg-gray-800 text-white font-mono text-sm px-3 py-1 rounded-lg shadow-sm">
            {formatTime(left)}
          </span>
        </div>
      </div>

      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-600" style={{width:`${pct}%`}}/>
      </div>
      <p className="text-xs text-center text-gray-600 mt-1">
        Pregunta {idx+1}/{total}
      </p>
    </header>
  );
}

/* ---------- componente principal ---------- */
export default function ExamenRunner(){
  const {id}   = useParams();
  const nav    = useNavigate();

  const [data,setData] = useState(null);   // {meta,preguntas}
  const [idx ,setIdx ] = useState(0);      // índice para navegación
  const [ans ,setAns ] = useState({});     // { <qid>: "A" }
  const [left,setLeft] = useState(null);
  const timerRef = useRef(null);

  /* fetch prueba --------------------------------------------------------- */
  useEffect(()=>{
    api.get(`/pruebas/${id}`)
       .then(r=>{
         setData(r.data);
         setLeft(r.data.meta.duracion_min*60);
         timerRef.current=setInterval(()=>setLeft(t=>t-1),1000);
       })
       .catch(e=>{
         alert(e.response?.data?.detail||e.message);
         nav("/app");
       });
    return ()=>clearInterval(timerRef.current);
  },[id,nav]);

  /* ordenar preguntas (una sola vez) */
  const preguntas = useMemo(()=>{
    if(!data) return [];
    return [...data.preguntas].sort(
      (a,b)=>ORDEN.indexOf(a.curso)-ORDEN.indexOf(b.curso)
    );
  },[data]);

  /* fin automático */
  const handleFinish = useCallback(async ()=>{
    clearInterval(timerRef.current);

    let correct=0, wrong=0, puntaje=0;
    preguntas.forEach(p=>{
      const sel = ans[p.id];            // ← usamos id real
      if(!sel) return;
      if(sel===p.respuesta_correcta){
        correct++; puntaje+=puntosPorCurso(p.curso,data.meta.tipo);
      }else wrong++;
    });
    const blancas = preguntas.length-(correct+wrong);
    const nota20  = Math.min(Math.round(puntaje*100)/100,20);

    /* guardar */
    try{
      await api.post(
        data.meta.tipo==="diagnostico"
          ? "/guardar-diagnostico/"
          : "/guardar-simulacro/",
        {
          nombre:"",
          correo:localStorage.getItem("email")||"",
          resultado:nota20,
          preguntas_correctas:correct,
          preguntas_incorrectas:wrong,
          preguntas_sin_responder:blancas,
          tiempo_usado:data.meta.duracion_min*60-left,
          numero:data.meta.numero,
          respuestas:JSON.stringify(ans)
        }
      );
    }catch(e){ console.error("Guardar:",e); }

    /* ir a resumen */
    nav("/app/resultado",{
      state:{nota:nota20,correctas:correct,incorrect:wrong,
             blancas,total:preguntas.length}
    });
  },[ans,data,left,nav,preguntas]);

  useEffect(()=>{ if(left===0) handleFinish(); },[left,handleFinish]);

  if(!data) return <p className="text-center mt-10">Cargando…</p>;

  const actual = preguntas[idx];
  const respondidas = Object.keys(ans).length;

  /* ---------- render ---------- */
  return(
    <div className="max-w-4xl mx-auto p-6">
      <Header meta={data.meta} idx={idx} total={preguntas.length} left={left}/>

      <div className="border rounded-xl p-6 shadow mb-6">
        <p className="mb-4 leading-relaxed">
          <span className="text-blue-600 mr-2">#{idx+1}</span>
          <LatexBlock>{actual.ejercicio}</LatexBlock>
        </p>

        {actual.imagen &&
          <img src={actual.imagen} alt="" className="mb-4 max-h-64 mx-auto"/>}

        {actual.alternativas.map(alt=>(
          <label key={alt.letra}
                 className={`block border rounded-md px-4 py-2 mb-3 cursor-pointer ${
                   ans[actual.id]===alt.letra
                     ? "border-blue-600 bg-blue-50"
                     : "hover:bg-gray-50"}`}>
            <input type="radio"
                   name={`q${actual.id}`}     // ← id único
                   className="mr-3"
                   checked={ans[actual.id]===alt.letra}
                   onChange={()=>setAns({...ans,[actual.id]:alt.letra})}/>
            <span className="mr-2">{alt.letra}.</span>
            {isUrl(alt.texto)
              ? (
                  <img
                    src={alt.texto}
                    alt={`alternativa ${alt.letra}`}
                    className="inline-block max-h-40 object-contain"
                  />
                )
              : (
                  <LatexBlock>{alt.texto}</LatexBlock>
                )}
          </label>
        ))}
      </div>

      <div className="flex justify-between">
        <button onClick={()=>setIdx(i=>i-1)} disabled={idx===0}
                className="px-4 py-2 rounded bg-gray-200 disabled:opacity-50">
          ← Anterior
        </button>
        {idx < preguntas.length-1 ? (
          <button onClick={()=>setIdx(i=>i+1)}
                  className="px-4 py-2 rounded bg-gray-200">
            Siguiente →
          </button>
        ):(
          <button onClick={handleFinish}
                  className="px-4 py-2 rounded bg-indigo-600 text-white">
            Finalizar
          </button>
        )}
      </div>

      <p className="text-sm text-center mt-4 text-gray-600">
        Respondidas {respondidas}/{preguntas.length}
      </p>
    </div>
  );
}
